import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiDownload, apiFetch } from "./client";
import { meQueryKey } from "./households";

/**
 * Downloads everything a household holds, as one JSON file (K-38).
 *
 * A different thing from the CSV export, and both are wanted: the CSV is for a
 * spreadsheet, this is for leaving. It reuses the same object-URL dance, and
 * revokes it for the same reason — a household with four years of records is not
 * a rounding error to pin in memory.
 */
export async function downloadHouseholdData(householdId: string): Promise<string> {
  const { blob, filename } = await apiDownload(
    `/api/v1/households/${householdId}/export/data`,
    "kontora-daten.json",
  );

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return filename;
}

/**
 * Deletes a household and everything in it.
 *
 * The confirmation is the household's own name rather than a yes, because this
 * is the only operation in Kontora with no way back.
 */
export function useEraseHousehold(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (confirm: string) =>
      apiFetch<void>(`/api/v1/households/${householdId}`, {
        method: "DELETE",
        body: { confirm },
      }),
    // Everything cached about this household is now about something that does
    // not exist.
    onSuccess: () => client.invalidateQueries(),
  });
}

/** What an account's erasure did, and what Kontora could not do. */
export interface ErasureReport {
  erased_at: string;
  households_erased: number;
  households_remaining: number;
  note: string;
}

export interface ErasePersonInput {
  confirm: string;
  erase_households: boolean;
}

/** Erases the signed-in person's own account. */
export function useErasePerson() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ErasePersonInput) =>
      apiFetch<ErasureReport>("/api/v1/me", { method: "DELETE", body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: meQueryKey }),
  });
}
